import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MlMap, type GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { listMyEvents } from '../platform/my-events-log';

/**
 * Public map — individual jolt locations.
 *
 * The server-side endpoint at /v1/events/public returns the actual
 * jolt locations inside the viewport (lat / lon / kind / confidence).
 * See backend PublicEventResource for the wire contract. This replaces
 * the prior cell-grid aggregation, which hid minority event kinds and
 * produced "buckets pop in and out" jitter as the user zoomed (see
 * issues #56 and #39).
 *
 * Privacy posture lives client-side: the 75 m home-radius trim on
 * upload drops events near trip start/end before they reach the
 * server (see issue #55). That's the privacy guarantee the public
 * map relies on; the server here just slices by bbox.
 *
 * The viewer's own events are still drawn as a separate gold-on-black
 * layer, sourced from this device's local store — so the user can see
 * which dots on the public map came from their own driving.
 *
 * Bbox-bound: on map moveend (debounced) we ask for events inside the
 * current viewport. Initial view defaults to the user's location at
 * zoom 14 if geolocation is granted, falling back to UK midlands at
 * zoom 6 if denied. URL params ?lat=&lon=&z= override both.
 */

interface PublicJolt {
  lat: number;
  lon: number;
  kind: 'pothole' | 'bump' | 'swerve' | string;
  confidence: number;
}

const TILES_URL = 'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png';
const TILES_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>';

type EventClass = 'pothole' | 'bump' | 'swerve';
const CLASS_COLOR: Record<EventClass, string> = {
  pothole: '#b03030',
  bump: '#2c8a4a',
  swerve: '#1d6fa6',
};
const CLASS_LABEL: Record<EventClass, string> = {
  pothole: 'Pothole',
  bump: 'Speed bump',
  swerve: 'Swerve',
};

/** Default view if no geolocation and no URL params: UK midlands. */
const FALLBACK_CENTER: [number, number] = [-1.5, 53.5];
const FALLBACK_ZOOM = 6;
/** Debounce window on map moveend → bbox fetch. Long-enough that
 *  continuous pan/zoom doesn't hammer the API. */
const FETCH_DEBOUNCE_MS = 350;

function eventsToFeatureCollection(events: PublicJolt[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: events.map((e, i) => ({
      type: 'Feature',
      id: i,
      geometry: { type: 'Point', coordinates: [e.lon, e.lat] },
      properties: {
        klass: e.kind,
        confidence: e.confidence,
      },
    })),
  };
}

function myEventsToFeatureCollection(): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: listMyEvents().map((e, i) => ({
      type: 'Feature',
      id: i,
      geometry: { type: 'Point', coordinates: [e.lon, e.lat] },
      properties: { eventGuid: e.eventGuid },
    })),
  };
}

/** Parse `?lat=&lon=&z=` from the URL. Returns null if not all present. */
function parseUrlView():
  | { center: [number, number]; zoom: number }
  | null {
  const params = new URLSearchParams(window.location.search);
  const lat = params.get('lat');
  const lon = params.get('lon');
  const z = params.get('z');
  if (!lat || !lon || !z) return null;
  const fLat = parseFloat(lat);
  const fLon = parseFloat(lon);
  const fZoom = parseFloat(z);
  if (![fLat, fLon, fZoom].every(Number.isFinite)) return null;
  return { center: [fLon, fLat], zoom: fZoom };
}

export function MapPage() {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [eventCount, setEventCount] = useState(0);
  const [mineCount, setMineCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Init map — set initial view from URL if present, else fallback to
  // UK midlands. Geolocation runs after map load and may animate to
  // the user's neighbourhood at zoom 14.
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const urlView = parseUrlView();
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          carto: {
            type: 'raster',
            tiles: [TILES_URL],
            tileSize: 256,
            attribution: TILES_ATTRIBUTION,
          },
        },
        layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
      },
      center: urlView?.center ?? FALLBACK_CENTER,
      zoom: urlView?.zoom ?? FALLBACK_ZOOM,
    });
    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.addControl(new maplibregl.FullscreenControl(), 'top-right');
    // Geolocate control: gives the user an explicit "📍 Find me" button
    // and also auto-triggers on map load below.
    const geo = new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: false, timeout: 5000 },
      trackUserLocation: false,
      showAccuracyCircle: false,
    });
    map.addControl(geo, 'top-right');

    map.on('load', () => {
      // Two sources: public events (everyone) + the visitor's own
      // events (this device only, from localStorage). Two layers
      // styled differently so the visitor can see their contributions
      // in context.
      map.addSource('events', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('mine', {
        type: 'geojson',
        data: myEventsToFeatureCollection(),
      });
      map.addLayer({
        id: 'event-circles',
        type: 'circle',
        source: 'events',
        paint: {
          'circle-color': [
            'match',
            ['get', 'klass'],
            'pothole', CLASS_COLOR.pothole,
            'bump', CLASS_COLOR.bump,
            'swerve', CLASS_COLOR.swerve,
            '#888888',
          ],
          // Fixed-size dots — one per actual jolt. No count-based
          // scaling because each feature is now a single event.
          'circle-radius': 5,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.85,
        },
      });
      map.addLayer({
        id: 'mine-circles',
        type: 'circle',
        source: 'mine',
        paint: {
          'circle-color': '#ffd400',
          'circle-radius': 5,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#1a1a1a',
          'circle-opacity': 0.95,
        },
      });
      mapRef.current = map;
      setMapReady(true);
      setMineCount(listMyEvents().length);

      // If the URL didn't pin a view, attempt geolocation now. Trigger
      // the maplibre control rather than calling navigator.geolocation
      // directly — the control handles UI feedback, error states, and
      // the iOS-Safari user-gesture quirks for us. On denial it
      // silently leaves the user at the fallback view.
      if (!urlView) geo.trigger();
    });

    // Click → popup with single-event summary (kind + confidence).
    map.on('click', 'event-circles', (e) => {
      const f = e.features?.[0];
      if (!f || f.geometry.type !== 'Point') return;
      const p = f.properties as Record<string, unknown>;
      const kind = String(p.klass);
      const confidence = Number(p.confidence);
      const label = (CLASS_LABEL as Record<string, string>)[kind] ?? kind;
      const lines: string[] = [`<strong>${label}</strong>`];
      if (Number.isFinite(confidence)) {
        lines.push(`Confidence: ${Math.round(confidence * 100)}%`);
      }
      // className opt-in to the joltstream-popup theme: dark background,
      // light foreground, matching tip + close-button colours. Without
      // it MapLibre's default light-on-light styling makes the text
      // unreadable.
      new maplibregl.Popup({ closeButton: true, className: 'joltstream-popup' })
        .setLngLat(f.geometry.coordinates as [number, number])
        .setHTML(lines.join('<br>'))
        .addTo(map);
    });
    map.on('mouseenter', 'event-circles', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'event-circles', () => { map.getCanvas().style.cursor = ''; });

    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  // Bbox fetch on moveend, debounced. Reruns whenever the viewport
  // changes. Events returned are stuffed straight into the GeoJSON
  // source.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let aborter: AbortController | null = null;

    const fetchEvents = () => {
      const b = map.getBounds();
      const minLat = b.getSouth();
      const maxLat = b.getNorth();
      const minLon = b.getWest();
      const maxLon = b.getEast();
      // Reject zoom-too-far queries (>10 deg span on either axis) so
      // we don't even attempt them — the server will 400 otherwise
      // and we'd just be making noise. Just leave whatever dots are
      // already shown in place.
      if (maxLat - minLat > 10 || maxLon - minLon > 10) {
        setLoading(false);
        return;
      }
      aborter?.abort();
      aborter = new AbortController();
      setLoading(true);
      const url =
        `/v1/events/public?minLat=${minLat}&maxLat=${maxLat}` +
        `&minLon=${minLon}&maxLon=${maxLon}`;
      fetch(url, { signal: aborter.signal })
        .then((r) => (r.ok ? r.json() : []))
        .then((events: PublicJolt[]) => {
          const src = map.getSource('events') as GeoJSONSource | undefined;
          if (src) src.setData(eventsToFeatureCollection(events));
          setEventCount(events.length);
        })
        .catch(() => {
          // Abort or network — ignore; existing dots stay shown.
        })
        .finally(() => setLoading(false));
    };

    const onMoveEnd = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fetchEvents, FETCH_DEBOUNCE_MS);
    };
    map.on('moveend', onMoveEnd);
    // Trigger an initial fetch — moveend won't fire on its own at mount.
    fetchEvents();

    return () => {
      map.off('moveend', onMoveEnd);
      if (timer) clearTimeout(timer);
      aborter?.abort();
    };
  }, [mapReady]);

  return (
    <div className="page map-page">
      <div className="map-page-header">
        <div>
          <h1>joltstream</h1>
          <p className="lede">Anonymous open road data.</p>
        </div>
        <p>
          <a className="cta" href="/">Start a journey →</a>
          <a className="cta-secondary" href="/download">Get the app</a>
        </p>
      </div>

      <details className="banner banner-beta" aria-label="Beta programme">
        <summary>
          <strong>Open beta</strong> — drivers wanted
        </summary>
        <p>
          Our mission is to gather anonymous data on road defects so councils
          can be held accountable for fixing them. The more drivers
          contribute, the harder the data is to ignore.{' '}
          <a href="mailto:beta@joltstream.app?subject=joltstream%20beta">
            Join the beta
          </a>{' '}
          or just <a href="/">start streaming data</a> — no signup needed.
        </p>
      </details>

      <div className="control-map-wrap">
        <div className="control-map" ref={mapContainerRef} />
        <details className="map-legend">
          <summary
            className="map-legend-summary"
            aria-label="Event types legend — click to expand"
          >
            {(['pothole', 'bump', 'swerve'] as EventClass[]).map((c) => (
              <span
                key={c}
                className="map-legend-swatch"
                style={{ background: CLASS_COLOR[c] }}
                title={CLASS_LABEL[c]}
                aria-hidden="true"
              />
            ))}
          </summary>
          <div className="map-legend-body">
            <div className="map-legend-title">Event types</div>
            {(['pothole', 'bump', 'swerve'] as EventClass[]).map((c) => (
              <div key={c} className="map-legend-row">
                <span
                  className="map-legend-swatch"
                  style={{ background: CLASS_COLOR[c] }}
                  aria-hidden="true"
                />
                <span>{CLASS_LABEL[c]}</span>
              </div>
            ))}
            <div className="map-legend-row" style={{ marginTop: '0.4rem' }}>
              <span
                className="map-legend-swatch"
                style={{ background: '#ffd400', border: '2px solid #1a1a1a' }}
                aria-hidden="true"
              />
              <span>Your contributions</span>
            </div>
          </div>
        </details>
      </div>

      <p className="hint">
        {loading
          ? 'Loading…'
          : `Showing ${eventCount} jolt${eventCount === 1 ? '' : 's'} in view` +
            (mineCount ? ` + ${mineCount} of your own contributions` : '') +
            '. Pan or zoom to see other areas.'}
      </p>
    </div>
  );
}
